"use client";

import { Fragment } from "react";

import Link from "next/link";
import { usePathname } from "next/navigation";

import { PERCORSO_GUIDA, conIlRitorno, percorsoAiuto } from "@/lib/guida";

/**
 * Le sezioni del sito, nella striscia sotto la banda blu, con **quella in cui
 * sei segnata**.
 *
 * Nata il 13 settembre 2026 da un'osservazione del proprietario:
 * l'intestazione era *«un po' raffazzonata»*. Aveva ragione, e il difetto era
 * strutturale prima che estetico — nella banda c'erano **otto elementi con lo
 * stesso peso**: data, selettore di spazio, Stato, Utenti, il nome, Guida, il
 * «?» ed Esci, tutti in una riga sola che andava a capo. Il commento nel foglio
 * di stile lo ammetteva già: non ci stavano.
 *
 * Adesso il pannello blu tiene solo **chi sei e dove sei** — il nome del sito,
 * la data, l'account — e le **sezioni** hanno una riga loro. Il blu torna a
 * essere il tema invece di una barra comandi.
 *
 * **Il guadagno vero non è l'ordine, è sapere dove sei.** Prima, aperto
 * «Utenti», niente sullo schermo lo diceva: il collegamento era identico agli
 * altri sette. Qui la voce corrente porta `aria-current="page"`, che è insieme
 * quello che disegna la sottolineatura e quello che un lettore di schermo
 * annuncia. Un solo attributo per tutti e due, e non due meccanismi che
 * possono separarsi.
 *
 * **«Guida» è l'ultimo figlio di questo stesso `<nav>`, non un elemento a
 * parte — di nuovo, dal 25 settembre 2026.** È la seconda sistemazione in due
 * giorni, e vale la pena raccontare perché la prima non bastava.
 *
 * Il 24 settembre «Guida» è uscita dall'elenco `voci` per diventare un punto
 * fermo, in un contenitore suo (`.barra-sezioni-destra`) accanto al `<nav>`,
 * tenuto a destra da un `justify-content: space-between` sul genitore
 * comune. Collaudandolo **dal telefono** il proprietario ha trovato il
 * difetto: *«si distinguono e guida si trova ma va su una riga nuova anche se
 * c'è posto prima»*. La causa era strutturale, non un dettaglio di stile —
 * `<nav>` è lui stesso un contenitore `flex-wrap`, e per l'algoritmo del
 * genitore **è un solo elemento**, con una dimensione «naturale» pari a
 * tutte le sue voci disposte su una riga sola, enorme. Quella dimensione,
 * anche se poi il `<nav>` si restringe e le sue voci vanno a capo per conto
 * loro, è quella con cui il genitore decide **se c'è ancora posto per il
 * prossimo elemento sulla stessa riga** — e non c'è mai, quindi «Guida»
 * finiva sempre su una riga a parte, qualunque cosa succedesse dentro il
 * `<nav>`. Un contenitore a parte per «Guida» non poteva funzionare: doveva
 * mancare, non spostarsi.
 *
 * **La correzione: «Guida» diventa una voce del flusso**, l'ultima, dentro
 * lo stesso `<nav>` e lo stesso `flex-wrap` delle altre — non nell'elenco
 * `voci` (quello resta suo, riordinato e con lo stacco, invariato da ieri),
 * ma disegnata qui subito dopo. Da lì in poi l'andare a capo lo decide lo
 * stesso algoritmo, voce per voce, come farebbe per una qualunque delle
 * altre dieci: se c'è posto sull'ultima riga la occupa, altrimenti scende.
 * `.sezione-guida` in `globals.css` le aggiunge `margin-left: auto`, che è il
 * pezzo che tiene **anche** l'ancoraggio a destra: quel margine assorbe tutto
 * lo spazio libero **a sinistra** di «Guida» sulla riga in cui cade — su uno
 * schermo largo, dove ci sta comodamente sulla stessa riga delle altre voci,
 * la spinge fino al bordo destro della striscia; su un telefono, se resta da
 * sola sull'ultima riga perché non c'è più posto prima, fa lo stesso lì. Un
 * meccanismo solo, **nessuna soglia di larghezza da scegliere e mantenere**:
 * il comportamento «ancorata» e quello «nel flusso» sono lo stesso calcolo
 * visto a due larghezze diverse, non due regole che si alternano.
 *
 * Resta comunque **un pezzo cliente in più**, `VoceGuida`, perché deve sapere
 * l'indirizzo corrente per calcolare dove porta e se è la voce accesa —
 * `usePathname` risponde già durante il disegno sul server, quindi anche
 * questa evidenziazione è **dentro l'HTML che arriva**, codice cliente o no.
 */
export function SezioniNavigazione({
  voci,
}: {
  voci: readonly {
    href: string;
    testo: string;
    /**
     * Se `true`, questa voce apre un **secondo gruppo**: prima di disegnarla si
     * disegna uno stacco. **24 settembre 2026**, quando la striscia è stata
     * riordinata in due gruppi — quello che si apre ogni giorno e quello che
     * riguarda la macchina e la famiglia — e serve un segno che li separi senza
     * essere un colore che vuol dire qualcosa.
     *
     * **Un elemento vero e non un margine**, perché un margine attaccato alla
     * prima voce del secondo gruppo sparisce o si ritrova a inizio riga quando
     * la striscia va a capo su telefono: qui invece lo stacco è un pezzo della
     * fila esattamente come una voce, quindi resta appena prima del gruppo
     * tecnico comunque vada a capo la fila — sia in fondo alla prima riga sia
     * in cima alla seconda. Vedi `.sezioni-stacco` in `globals.css`.
     */
    nuovoGruppo?: boolean;
  }[];
}) {
  const percorso = usePathname() ?? "/";

  /*
   * **Si accende una voce sola: quella che combacia di più.**
   *
   * `startsWith` con la barra in fondo e non `startsWith` nudo: una voce con
   * pagine figlie (`/scuola/orario`) deve restare accesa entrando in una
   * pagina più profonda, ma senza quella barra un futuro `/statistiche`
   * accenderebbe anche «Stato».
   *
   * E fra le voci che combaciano vince **la più lunga**, altrimenti due si
   * accendono insieme: dalla 9c il menu ha sia «Orario» (`/scuola/orario`) sia
   * «Scuola» (`/scuola`), e `/scuola/orario` comincia con tutte e due. Senza
   * questa riga il menu direbbe che sei in due posti.
   */
  const combacia = (href: string) => percorso === href || percorso.startsWith(`${href}/`);
  const acceso = voci
    .map((voce) => voce.href)
    .filter(combacia)
    .sort((uno, altro) => altro.length - uno.length)[0];

  return (
    <nav className="sezioni" aria-label="Sezioni del sito">
      {voci.map((voce) => {
        const qui = voce.href === acceso;

        return (
          <Fragment key={voce.href}>
            {voce.nuovoGruppo === true && (
              // Decorativo: i due gruppi restano un'unica lista di
              // collegamenti per chi usa un lettore di schermo, nell'ordine in
              // cui si leggono. Lo stacco è per l'occhio, non aggiunge né
              // toglie niente al percorso.
              <span className="sezioni-stacco" aria-hidden="true" />
            )}
            <Link href={voce.href} className="sezione-voce" aria-current={qui ? "page" : undefined}>
              {voce.testo}
            </Link>
          </Fragment>
        );
      })}
      {/* «Guida»: non è nell'elenco `voci` — resta un punto fermo, non
          l'ultima di una fila che cresce — ma è nello stesso `<nav>` e nello
          stesso flusso `flex-wrap`, per la ragione spiegata sopra. */}
      <VoceGuida />
    </nav>
  );
}

/**
 * **«Guida», l'ultima voce del `<nav>` delle sezioni — ma non una voce
 * dell'elenco `voci`.**
 *
 * **Nata il 24 settembre 2026**, quando il proprietario ha chiesto di
 * portarla fuori dall'elenco — *«la sposterei a destra»* — e **risistemata il
 * 25**, quando dal telefono ha trovato il contenitore a parte rotto (vedi il
 * commento sopra `SezioniNavigazione`). La ragione di fondo non è cambiata:
 * «Guida» non è una voce come le altre, cresce o si accorcia l'elenco.
 *
 * **Perché un punto fermo vale più di un ultimo posto.** «Ultima» è una
 * posizione che si sposta ogni volta che ne nasce una nuova: l'elenco a
 * sinistra è arrivato a dieci voci e si muoverà ancora con i voti, il piano
 * di studio, i riassunti, gli esercizi. Una cosa che si cerca **quando si è
 * in difficoltà** deve stare in un punto che non si sposta, non nell'ultimo
 * posto di una fila che cambia lunghezza — e infatti «Guida» **non fa parte**
 * di quella fila: è disegnata a mano dopo di lei, sempre per ultima, e la sua
 * posizione a destra viene dal CSS (`.sezione-guida`, `margin-left: auto`),
 * non dall'ordine dell'elenco.
 *
 * **La logica non cambia, solo il posto.** Porta alla pagina di guida
 * **della schermata in cui si è**, e all'indice solo quando quella schermata
 * non ne ha una — la stessa regola di `percorsoAiuto`, invariata da quando è
 * nata il 13 settembre 2026. Anche l'evidenziazione resta la stessa idea:
 * accesa quando l'indirizzo comincia per `/guida`, cioè quando si è dentro
 * **una qualunque** pagina di guida, non solo quella corrente — la voce dice
 * «sei nella sezione Guida», non «sei su questa pagina di guida precisa».
 *
 * Isolata dall'elenco di `SezioniNavigazione` invece di restarci con un
 * campo che la distingue: da sola non ha bisogno né dello stacco né del
 * confronto con le altre voci per sapere se accendersi.
 */
export function VoceGuida() {
  const percorso = usePathname() ?? "/";
  const qui = percorso === PERCORSO_GUIDA || percorso.startsWith(`${PERCORSO_GUIDA}/`);

  return (
    <Link
      href={conIlRitorno(percorsoAiuto(percorso), percorso)}
      className="sezione-voce sezione-guida"
      aria-current={qui ? "page" : undefined}
    >
      Guida
    </Link>
  );
}
